import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Assert } from '@cotera/utilities';
import { useUntenantedClient } from '@cotera/client/app/stores/org';
import { atcQueryKeys } from '../query-keys';

/**
 * Fetches organization names for a list of org IDs and returns a Map<orgId, name>.
 *
 * Org names are decorative — callers fall back to the raw id when a name is
 * missing. So this intentionally does NOT suspend or throw on failure: a failed
 * or in-flight lookup just yields an empty map, letting the surrounding
 * dashboard render (with raw ids) instead of crashing the whole page.
 */
export function useOrgNames(orgIds: string[]): Map<string, string> {
  const client = useUntenantedClient();

  const { data: orgs } = useQuery({
    queryFn: async () => {
      if (orgIds.length === 0) {
        return [];
      }
      const res = await client.atcVisibility.getOrgsByIds({ orgIds });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.organizationsByIds(orgIds),
    enabled: orgIds.length > 0,
  });

  return useMemo(
    () => new Map(orgs?.map((org) => [org.id, org.name]) ?? []),
    [orgs]
  );
}

/**
 * Same as useOrgNames but uses useQuery (non-suspense).
 * Returns undefined while loading.
 */
export function useOrgNamesLazy(
  orgIds: string[],
  enabled = true
): Map<string, string> | undefined {
  const client = useUntenantedClient();

  const { data: orgs } = useQuery({
    queryFn: async () => {
      if (orgIds.length === 0) {
        return [];
      }
      const res = await client.atcVisibility.getOrgsByIds({ orgIds });
      return Assert.assertOk(res);
    },
    queryKey: atcQueryKeys.organizationsByIds(orgIds),
    enabled: enabled && orgIds.length > 0,
  });

  return useMemo(() => {
    if (orgs === undefined) {
      return undefined;
    }
    return new Map(orgs.map((org) => [org.id, org.name]));
  }, [orgs]);
}
